import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { ConfigKey, ConfigurationService } from '../configuration/configuration.service';

@Injectable()
export class FeatureToggleService {
  private featureToggleCache: { [ key: string ]: boolean } = {};

  constructor(private configService: ConfigurationService) {}

  public get isCoBorrowerFlowEnabled(): Observable<boolean> {
    return this.getValueByKey(ConfigKey.CoborrowerEnabled);
  }

  public get isFinicityEnabled(): Observable<boolean> {
    return this.getValueByKey(ConfigKey.FinicityBankLinkingEnabled);
  }

  public get isFormFreeEnabled(): Observable<boolean> {
    return this.getValueByKey(ConfigKey.FormFree_Enabled);
  }

  public get isHomeImprovementEnabled(): Observable<boolean> {
    return this.getValueByKey(ConfigKey.HomeImprovementEnabled);
  }

  /**
   * Used to toggler between Government survey questionaire and new HMDA required survey changes
   */
  public get isHmdaEnabled(): Observable<boolean> {
    return this.getValueByKey(ConfigKey.HmdaEnabled);
  }

  public get isMitekEnabled(): Observable<boolean> {
    return this.getValueByKey(ConfigKey.MitekEnabled);
  }

  public get isPersonalLoansBetaEnabled(): Observable<boolean> {
    return this.getValueByKey(ConfigKey.PersonalLoansBeta_Enabled);
  }

  public get isPersonalLoansDashboardEnabled(): Observable<boolean> {
    return this.getValueByKey(ConfigKey.PersonalLoansDashboard_Enabled);
  }

  // ConfigKey.PersonalLoans_Enabled
  public get isPersonalLoansEnabled(): Observable<boolean> {
    return this.getValueByKey(ConfigKey.PersonalLoans_Enabled);
  }

  // Check if the Presentation of Offers feature should be enabled/disabled.
  // https://confluence.loandepot.com/display/ULA/Feature%3A+Presentation+of+Offers
  public get isPricingEnabled(): Observable<boolean> {
    return this.getValueByKey(ConfigKey.PricingEnabled);
  }

  public get isStateLendingEnabled(): Observable<boolean> {
    return this.getValueByKey(ConfigKey.StateLendingEnabled);
  }

  public get isTestUiEnabled(): Observable<boolean> {
    return this.getValueByKey(ConfigKey.ULATest_UI_Route_Enabled);
  }

  // Check if The Work Number Service should be enabled/disabled.
  public get isTWNEnabled(): Observable<boolean> {
    return this.getValueByKey(ConfigKey.TWNEnabled);
  }

  public get isUnifiedLoginEnabled(): Observable<boolean> {
    return this.getValueByKey(ConfigKey.UnifiedLoginEnabled);
  }

  private getValueByKey(key: ConfigKey): Observable<boolean> {
    let resultObserver;

    if (this.featureToggleCache[key] === true || this.featureToggleCache[key] === false) {

      // Feature-enabled result is cached - return the cached value.
      resultObserver = of(this.featureToggleCache[key]);
    } else {

      // Feature-enabled result is not cached.

      // Fetch a fresh value from the backend.
      resultObserver = this.configService.getConfigValueByKey(key)
        .map(this.stringToBool);

      // Cache the value.
      resultObserver.do((isEnabled: boolean) => this.setCache(key, isEnabled));
    }

    return resultObserver;
  }

  private setCache(key: ConfigKey, isEnabled: boolean): void {
    this.featureToggleCache[key] = isEnabled;
  }

  private stringToBool(s: string): boolean {
    try {
      return s.toLowerCase() === 'true';
    } catch (e) {
      throw e;
    }
  }
}
